import { type FieldValues, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { mergeDeepWith } from 'ramda';
import {
  calcPayoutRate,
  calcChargeHours,
  calcPayHours,
} from 'resources/rate_settings/rateSettingsCalculations';
import _ from 'lodash';
import { calculatedFieldPath } from 'resources/rate_settings/utils/fieldsVisibility';
import { PARENT_SETTINGS_FIELD } from './constants';

const W2_BURDEN_MULTIPLIER_FIELDS = [
  'social_security_burden_multiplier',
  'medicare_burden_multiplier',
  'federal_unemployment_percent_burden_multiplier',
  'state_unemployment_percent_burden_multiplier',
] as const;

// Employer-burden sum for W2 Local math: own form values win, blank ones fall
// back to the parent settings (create forms carry no pre-filled burdens).
export const resolveW2BurdenMultipliers = (watch: UseFormWatch<FieldValues>) => {
  const formW2 = watch('settings.w2') || {};
  const parentW2 = watch(PARENT_SETTINGS_FIELD)?.w2 || {};
  return W2_BURDEN_MULTIPLIER_FIELDS.reduce((acc, field) => {
    const own = formW2[field];
    const value = own === '' || own === null || own === undefined ? parentW2[field] : own;
    return acc + (Number(value) || 0);
  }, 0);
};

type OvertimeConfigurationValues = {
  charge_double_overtime_multiplier: number;
  charge_max_daily_regular_hours: number;
  charge_max_daily_overtime_hours: number;
  charge_overtime_multiplier: number;
  payout_max_daily_regular_hours: number;
  payout_max_daily_overtime_hours: number;
  payout_overtime_multiplier: number;
  payout_double_overtime_multiplier: number;
  blended_rate_markup?: number;
};

type UpdateProRateCentsProps = {
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  source: string;
  marginPercent: number;
  rateCents: number;
  inheritedSettings: any;
  overtimeConfigurationValues?: OvertimeConfigurationValues;
};

type UpdateChargeRateCentsProps = {
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  source: string;
  professionalRateCents: number;
  marginPercent: number;
  blendedMarkup: number;
  inheritedSettings: any;
  overtimeConfigurationValues?: OvertimeConfigurationValues;
};

type GetInheritedSettingsProps = {
  source: string;
  watch: UseFormWatch<FieldValues>;
  inheritedSettings: any;
};

type CalculateNewTotalMarginPercentW2Props = {
  watch: UseFormWatch<FieldValues>;
  source: string;
  chargeRate: number;
  professionalRate: number;
  setValue: UseFormSetValue<FieldValues>;
  // Record-based path reader; defaults to watch for legacy (non-controller) callers.
  getRecordValue?: (path: string) => unknown;
  solved?: any;
};

export const getInheritedSettings = ({
  source,
  inheritedSettings,
  watch,
}: GetInheritedSettingsProps) => {
  const formData = watch(source);
  const { tab } = getClassificationFromSource(source);

  if (inheritedSettings?.ten99 || inheritedSettings?.w2) {
    inheritedSettings = _.get(inheritedSettings, tab);
  }

  return mergeDeepWith(
    (a, b) => (a === '' || a === null || a === undefined ? b : a),
    formData || {},
    inheritedSettings || {},
  );
};

export const getSourceAndLabel = (source: string, useBaseMargin: boolean = false) => {
  const typeMapping = {
    'w2.local': useBaseMargin
      ? {
          source: `${source}.base_margin_percent_w2`,
          label: 'Base Margin Percent W2',
          field: 'base_margin_percent_w2',
        }
      : {
          source: `${source}.total_margin_percent_w2`,
          label: 'Total Margin Percent W2',
          field: 'total_margin_percent_w2',
        },
    'w2.travel': {
      source: `${source}.margin_percent_w2_travel`,
      label: 'Margin Percent W2 Travel',
      field: 'margin_percent_w2_travel',
    },
    ten99: {
      source: `${source}.margin_percent_1099`,
      label: 'Margin Percent 1099',
      field: 'margin_percent_1099',
    },
  };

  const matchedType = Object.keys(typeMapping).find((type) => source.includes(type));
  return matchedType ? typeMapping[matchedType] : { source: '', label: '' };
};

// verify if w2 travel rate configuration exists. And if not, use w2 local rate configuration
const getSettingsWithFallback = (
  mergedSettings: any,
  w2LocalMergedSettings: any,
  source: string,
) => {
  const { use_blended_rate, blended_rates, non_blended_rates } = mergedSettings || {};
  let settingsToUse = use_blended_rate ? blended_rates : non_blended_rates;

  if (
    (!settingsToUse?.charge_double_overtime_multiplier ||
      !settingsToUse?.charge_max_daily_regular_hours ||
      !settingsToUse?.charge_max_daily_overtime_hours ||
      !settingsToUse?.charge_overtime_multiplier ||
      !settingsToUse?.payout_max_daily_regular_hours ||
      !settingsToUse?.payout_max_daily_overtime_hours ||
      !settingsToUse?.payout_overtime_multiplier ||
      !settingsToUse?.payout_double_overtime_multiplier) &&
    source.includes('w2')
  ) {
    const {
      use_blended_rate: w2UseBlendedRate,
      blended_rates: w2BlendedRates,
      non_blended_rates: w2NonBlendedRates,
    } = w2LocalMergedSettings || {};
    settingsToUse = w2UseBlendedRate ? w2BlendedRates : w2NonBlendedRates;
  }

  return settingsToUse || {};
};

export const updateProRateCents = ({
  setValue,
  watch,
  source,
  rateCents,
  marginPercent,
  inheritedSettings,
  overtimeConfigurationValues,
}: UpdateProRateCentsProps) => {
  const { level } = getClassificationFromSource(source);

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const w2LocalMergedSettings = getInheritedSettings({
    source: `${level}.w2.local`,
    inheritedSettings,
    watch,
  });

  const settingsToUse =
    overtimeConfigurationValues ||
    getSettingsWithFallback(mergedSettings, w2LocalMergedSettings, source);

  const {
    charge_double_overtime_multiplier,
    charge_max_daily_regular_hours,
    charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  } = settingsToUse;

  const { payoutRate } = calcPayoutRate({
    hours: 12,
    chargeRate: rateCents,
    charge_double_overtime_multiplier,
    margin_percent: marginPercent,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
    excludeTenCentReduction: true,
  });
  setValue(`${source}.pro_rate_cents`, payoutRate);
};

// TODO: figure out if this is still needed
export const updateChargeRateCents = ({
  setValue,
  watch,
  source,
  professionalRateCents,
  marginPercent,
  inheritedSettings,
  overtimeConfigurationValues,
  blendedMarkup,
}: UpdateChargeRateCentsProps) => {
  const { level } = getClassificationFromSource(source);

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const w2LocalMergedSettings = getInheritedSettings({
    source: `${level}.w2.local`,
    inheritedSettings,
    watch,
  });

  const isUsingBlendedRates = mergedSettings?.use_blended_rate;

  const settingsToUse =
    overtimeConfigurationValues ||
    getSettingsWithFallback(mergedSettings, w2LocalMergedSettings, source);

  const {
    charge_double_overtime_multiplier,
    charge_max_daily_regular_hours,
    charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  } = settingsToUse;

  const hours = 12;

  const chargeHours = calcChargeHours({
    hours,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  });

  if (payHours === 0) {
    return;
  }
  const normalizedMargin = Number(marginPercent || 0);
  if (normalizedMargin >= 1) {
    return;
  }

  const appliedChargeRate = Math.round(
    (professionalRateCents * payHours) / (chargeHours * (1 - normalizedMargin)),
  );
  const chargeRateWithoutMarkup = Math.round(appliedChargeRate / (1 + blendedMarkup));
  const chargeRateWithMarkup = Math.round(appliedChargeRate * (1 + blendedMarkup));

  if (isUsingBlendedRates) {
    setValue(`${source}.blended_rate_cents`, appliedChargeRate);
    setValue(calculatedFieldPath(source, 'charge_rate_cents', watch), chargeRateWithoutMarkup);
  } else {
    setValue(calculatedFieldPath(source, 'charge_rate_cents', watch), appliedChargeRate);
    !_.isNil(blendedMarkup) && setValue(`${source}.blended_rate_cents`, chargeRateWithMarkup);
  }
};

export const getClassificationFromSource = (source: string) => {
  const [level, hash, subhash] = source.split('.');
  const tab = `${hash}${subhash ? `.${subhash}` : ''}`;

  return {
    level,
    tab,
  };
};

export const calculateNewTotalMarginPercentW2 = ({
  watch,
  chargeRate,
  professionalRate,
  source,
  setValue,
  getRecordValue,
}: CalculateNewTotalMarginPercentW2Props) => {
  // Professional and charge rates are both final rates which include any blended markup
  const totalMarginPercentW2 = 1 - professionalRate / chargeRate;

  if (totalMarginPercentW2 && !isNaN(Number(totalMarginPercentW2))) {
    setValue(
      calculatedFieldPath(source, 'total_margin_percent_w2', getRecordValue ?? watch),
      totalMarginPercentW2.toFixed(5),
    );
  }

  return totalMarginPercentW2;
};
